// NOTE: Commented out Demo potential data. Should add it back when we have that data with us from backend.

import React, { useState, useMemo } from "react";
import Button from "../../../components/Button";
import Section from "../../../components/Section";
import Table from "../../../components/Table";
import Card from "../../../components/Card";
import StatusBar from "../../../components/StatusBar";
import PieChart from "../../../components/PieChart";
import ToggleSwitch from "../../../components/ToggleSwitch";
import PerformanceBarGraph from "./PerformanceBarGraph";
import CategoryTrendTable from "./CategoryTriendTable";
import { KeywordData, KeywordReport, PerformanceReport, SeoGraphs, SeoReportResponse, ShareOfVoiceReport, TableEntry } from "../../../services/seo-report";

// Utility function to capitalize the first letter of each word
const capitalizeFirstLetter = (string: string) => {
    return string
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

enum SeoReportSections {
    Performance = "Performance",
    BusinessCriticalKeywords = "Business Critical Keywords",
    TechIssues = "Major Tech Issues",
    ShareOfVoice = "Share of Voice",
}

const PerformanceSection = ({
    performanceReport,
}: {
    performanceReport: PerformanceReport;
}) => {
    // Dynamically extract graph data from the API response
    // remove ----------------------------------------------------------------------
    // const graphData = Object.keys(performanceReport.mom_trend).map((month) => ({
    //   name: month,
    //   leads: performanceReport.mom_trend[month].leads || 0,
    //   all_clicks: performanceReport.mom_trend[month].all_clicks || 0,
    //   branded_clicks: performanceReport.mom_trend[month].branded_clicks || 0,
    //   non_branded_clicks:
    //     performanceReport.mom_trend[month].non_branded_clicks || 0,
    // }));

    return (
        <div className="space-y-8">
            <h2 className="text-xl font-semibold mb-2">Performance Report</h2>
            <div className="flex justify-between">
                <Card flow title="Leads">
                    {performanceReport.leads}
                </Card>
                <Card flow title="Traffic">
                    {performanceReport.traffic}
                </Card>
                <Card flow title="Bofu Traffic">
                    {performanceReport.bofu_traffic || "-"}
                </Card>
                <Card flow title="Impressions: Bofu Keywords">
                    {performanceReport.bofu_impression || "-"}
                </Card>
            </div>

            <h3 className="text-lg font-semibold mb-2">Leads Target</h3>
            <div className="flex">
                {performanceReport.targets.map((target, index) => (
                    <Card key={index}>
                        <StatusBar data={target} />
                    </Card>
                ))}
            </div>

            {/* Line chart for all_clicks, branded_clicks, and non_branded_clicks */}
            {/* <div>
        <h3 className="text-lg font-semibold mb-2">Clicks Trend</h3>
        
          <LineChart
            data={graphData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="all_clicks" stroke="#82ca9d" />
            <Line type="monotone" dataKey="branded_clicks" stroke="#ffc658" />
            <Line
              type="monotone"
              dataKey="non_branded_clicks"
              stroke="#ff7300"
            />
          </LineChart>
       
      </div> */}

            <div>
                <h3 className="text-lg font-semibold mb-2">Category Trend</h3>
                <CategoryTrendTable
                    categoryTrendData={performanceReport.category_trend}
                />
            </div>

            <div>
                <h3 className="text-lg font-semibold mb-2">Performance Bar Graph</h3>
                <PerformanceBarGraph performanceReport={performanceReport} />
            </div>
        </div>
    );
};

const BusinessCriticalKeywordsSection = ({
    keywordReport,
}: {
    keywordReport: KeywordReport;
}) => {
    const months = useMemo(() => {
        const monthSet = new Set<string>();
        keywordReport.keyword_data.forEach((item) => {
            Object.keys(item.position).forEach((month) => {
                monthSet.add(month);
            });
        });
        return Array.from(monthSet);
    }, [keywordReport.keyword_data]);

    const columns = [
        {
            key: "keyword",
            header: "Keyword",
        },
        {
            key: "pageTargetingKeyword",
            header: "Page Targeting Keyword",
            render: (row: KeywordData) => (
                <a
                    href={row.pageTargetingKeyword}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {row.pageTargetingKeyword || "-"}
                </a>
            ),
        },
        // NOTE: ADD IT BACK WHEN WE HAVE THE DEMO POTENTIAL DATA
        // {
        //   key: "demoPotential",
        //   header: "Demo Potential",
        // },
        ...months.map((month) => ({
            key: `position.${month}`,
            header: `${month} Rank`,
            render: (row: KeywordData) => row.position[month] || "-",
        })),
    ];

    return (
        <div>
            <h2 className="text-xl font-semibold mb-2">
                Business Critical Keywords Report
            </h2>
            <Table
                data={keywordReport.keyword_data}
                columns={columns}
                fixedColumns={2}
                noMaxHeight={true}
                compact={true}
            />
        </div>
    );
};

const TechIssuesSection = ({
    performanceReport,
}: {
    performanceReport: PerformanceReport;
}) => {
    const techIssue = performanceReport.tech_issue;

    // Transform techIssue into list with json objects format
    const techIssueData = Object.entries(techIssue).map(([category, data]) => ({
        Category: capitalizeFirstLetter(category),
        ...data,
    }));

    const dynamicKeys = Object.keys(techIssue[Object.keys(techIssue)[0]]);
    const columns = [
        { key: "Category", header: "Category" },
        ...dynamicKeys.map((key) => ({ key: key, header: key })),
    ];

    return (
        <div>
            <h2 className="text-xl font-semibold mb-2">Major Tech Issues Report</h2>
            <Table
                data={techIssueData}
                columns={columns}
                noMaxHeight={true}
                compact={true}
                sortBy="Leads"
            />
        </div>
    );
};

const ShareOfVoiceSection = ({
    shareOfVoiceReport,
}: {
    shareOfVoiceReport: ShareOfVoiceReport | null; // Allow null for empty state
}) => {
    const [showRefined, setShowRefined] = useState(false);

    // Check if the shareOfVoiceReport is empty or undefined
    if (!shareOfVoiceReport || !shareOfVoiceReport.traffic_pie_chart) {
        return (
            <div className="text-center py-8">
                <h2 className="text-xl font-semibold mb-2">Share of Voice Report</h2>
                <p className="text-gray-500">
                    No data has been generated for this report yet.
                </p>
            </div>
        );
    }

    // Debugging statement to check the value of traffic_pie_chart
    console.log("Traffic Pie Chart Data:", shareOfVoiceReport.traffic_pie_chart);

    // Define table configurations
    const tableConfigs = [
        {
            title: "Page Type",
            key: "page_type",
            headerKey: "Page Type",
        },
        {
            title: "Topics",
            key: "topics",
            headerKey: "Topic",
        },
        {
            title: "Persona",
            key: "persona",
            headerKey: "Persona",
        },
        {
            title: "Business Segment",
            key: "business_segment",
            headerKey: "Segment",
        },
    ];

    return (
        <div className="space-y-8">
            {/* Traffic Distribution Section */}
            <div>
                <h2 className="text-xl font-semibold mb-2">Traffic Distribution</h2>
                <div className="grid grid-cols-2 gap-4">
                    {/* Pie Chart for Traffic */}
                    <div className="p-4 rounded-lg border">
                        <h3 className="font-normal text-lg mb-2">Traffic</h3>
                        <PieChart
                            data={shareOfVoiceReport.traffic_pie_chart.map((chart) => ({
                                name: chart.competitor_name,
                                value: chart.pie_chart_data.traffic,
                            }))}
                        />
                    </div>
                    {/* Pie Chart for BoFu Traffic */}
                    <div className="p-4 rounded-lg border">
                        <h3 className="font-normal text-lg mb-2">BoFu Traffic</h3>
                        <PieChart
                            data={shareOfVoiceReport.traffic_pie_chart.map((chart) => ({
                                name: chart.competitor_name,
                                value: chart.pie_chart_data.bofu_traffic,
                            }))}
                        />
                    </div>
                </div>
            </div>

            {/* Graphs Section */}
            <h2 className="text-xl font-semibold">Competitor Historical Analysis</h2>
            <div className="grid grid-cols-2 gap-4">
                {[
                    { title: "Traffic", metric: "traffic" },
                    { title: "BoFu Traffic", metric: "bofuTraffic" },
                    { title: "# Pages", metric: "pages" },
                    { title: "# Backlinks", metric: "backlinks" },
                    {
                        title: "# BoFu Keyword Impressions",
                        metric: "bofuKeywordImpressions",
                    },
                    {
                        title: "# Keywords with Under 10 Rankings",
                        metric: "keywordRankingUnder10",
                    },
                ].map(({ title, metric }, metricIndex) => (
                    <div key={metricIndex} className="p-4 rounded-lg border">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-normal text-lg">{title}</h3>
                        </div>
                        <img
                            src={shareOfVoiceReport.graphs[metric as keyof SeoGraphs]}
                            alt={title}
                            className="w-full h-auto rounded"
                        />
                    </div>
                ))}
            </div>

            {/* Tables Section */}
            <div className="flex py-4 border-t border-gray-200 justify-between">
                <h2 className="text-xl font-semibold mb-2">Segment Report</h2>
                <ToggleSwitch
                    checked={showRefined}
                    onCheckedChange={setShowRefined}
                    labelOn="Showing Refined Data"
                    labelOff="Showing Original Data"
                />
            </div>

            <div className="space-y-8">
                {tableConfigs.map((config) => (
                    <div key={config.key}>
                        <h3 className="text-lg font-semibold mb-2">{config.title}</h3>
                        <Table<TableEntry>
                            data={
                                showRefined
                                    ? shareOfVoiceReport.refined_table[
                                    config.key as keyof typeof shareOfVoiceReport.refined_table
                                    ]
                                    : shareOfVoiceReport.table[
                                    config.key as keyof typeof shareOfVoiceReport.table
                                    ]
                            }
                            columns={[
                                { key: "category", header: config.headerKey },
                                { key: "leads", header: "Leads" },
                                { key: "traffic", header: "Traffic" },
                                { key: "no_of_pages", header: "# of Pages" },
                                { key: "backlinks", header: "Backlinks" },
                            ]}
                            sortBy="leads"
                            noMaxHeight={true}
                            compact={true}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const ViewSeoReport: React.FC<{ seoReportResponse?: SeoReportResponse }> = ({
    seoReportResponse,
}) => {

    const [activeTab, setActiveTab] = useState(SeoReportSections.Performance);

    if (!seoReportResponse) {
        return <div>Loading SEO report data...</div>;
    }

    const performanceReport = seoReportResponse["performance-report"][0];
    const keywordReport = seoReportResponse["keyword-report"][0];
    const shareOfVoiceReport = seoReportResponse["share-of-voice-report"][0];

    return (
        <div>
            <Section
                // title="Choose Section"
                compact={true}
                titleSize="sm"
                className="text-center"
                titleClassName="text-center"
            >
                <Button
                    onClick={() => setActiveTab(SeoReportSections.Performance)}
                    className="mr-2"
                    primary={activeTab === SeoReportSections.Performance}
                >
                    {SeoReportSections.Performance}
                </Button>
                <Button
                    onClick={() =>
                        setActiveTab(SeoReportSections.BusinessCriticalKeywords)
                    }
                    className="mr-2 h-10"
                    primary={activeTab === SeoReportSections.BusinessCriticalKeywords}
                >
                    {SeoReportSections.BusinessCriticalKeywords}
                </Button>
                <Button
                    onClick={() => setActiveTab(SeoReportSections.TechIssues)}
                    className="mr-2"
                    primary={activeTab === SeoReportSections.TechIssues}
                >
                    {SeoReportSections.TechIssues}
                </Button>
                <Button
                    onClick={() => setActiveTab(SeoReportSections.ShareOfVoice)}
                    className="mr-2"
                    primary={activeTab === SeoReportSections.ShareOfVoice}
                >
                    {SeoReportSections.ShareOfVoice}
                </Button>
            </Section>
            <Section>
                {activeTab === SeoReportSections.Performance && performanceReport && (
                    <PerformanceSection performanceReport={performanceReport} />
                )}
                {activeTab === SeoReportSections.BusinessCriticalKeywords &&
                    keywordReport && (
                        <BusinessCriticalKeywordsSection keywordReport={keywordReport} />
                    )}
                {activeTab === SeoReportSections.TechIssues && (
                    <TechIssuesSection performanceReport={performanceReport} />
                )}
                {activeTab === SeoReportSections.ShareOfVoice && (
                    <ShareOfVoiceSection shareOfVoiceReport={shareOfVoiceReport} />
                )}
            </Section>
        </div>
    );
};

export default ViewSeoReport;
